import { RoofTypeName } from '../../types'
import { AttachmentOption, UltraRailMountingType } from './UltraRailTypes'

export const ROOF_ATTACHMENT_INPUT_LABEL = 'Roof Attachment'

export const ATTACHMENTS_PER_STYLE: Record<UltraRailMountingType, string[]> = {
  Rafter: [
    '242-10056',
    '242-10057',
    '242-10058',
    '242-01220',
    '242-01223',
    '242-01239',
    '242-01247',
    '242-01248',
    '242-02158',
    '242-02163',
    '242-02167',
    '242-02729',
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-10038',
    '242-10039-USA',
    '242-10041-USA',
    '242-10044-USA',
  ],
  Decking: [
    '242-10057',
    '242-10058',
    '242-01248',
    '242-02729',
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10038',
    '242-10039-USA',
    '242-10041-USA',
    '242-10044-USA',
  ],
  Metal: ['242-05159', '242-10039-USA', '242-10041-USA', '242-10042-USA', '242-10043-USA', '242-10044-USA'],
}

/** Roof type → compatible attachment SKUs (Slab "Roof compatibility and default attachment codes"). */
const ROOF_TYPE_COMPATIBLE_ATTACHMENTS: Partial<Record<RoofTypeName, string[]>> = {
  'Composition / Asphalt Shingle': [
    '242-10056',
    '242-10057',
    '242-10058',
    '242-01220',
    '242-02163',
    '242-02167',
    '242-01223',
    '242-01239',
    '242-10038',
  ],
  'Flat Concrete': ['242-10016', '242-10017', '242-10018', '242-01223', '242-01239'],
  'Flat Foam': ['242-10016', '242-10017', '242-10018', '242-01223', '242-01239'],
  'Membrane EPDM': [
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-01223',
    '242-01239',
  ],
  'Membrane PVC': [
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-01223',
    '242-01239',
  ],
  'Membrane TPO': [
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-01223',
    '242-01239',
  ],
  'Metal Shingle': ['242-10056', '242-01223', '242-01239', '242-02158', '242-10038', '242-10041-USA'],
  'Metal Standing Seam': ['242-05159', '242-10043-USA', '242-01223', '242-01239', '242-02158', '242-10041-USA'],
  'Metal Tin': ['242-10039-USA', '242-01223', '242-01239', '242-02158', '242-10041-USA'],
  'Rolled Asphalt': [
    '242-10056',
    '242-10057',
    '242-10058',
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-01223',
    '242-01239',
    '242-10038',
  ],
  'Tar and Gravel / Bitumen': [
    '242-10016',
    '242-10017',
    '242-10018',
    '242-10020',
    '242-10021',
    '242-10022',
    '242-01223',
    '242-01239',
  ],
  'Tile Clay': ['242-01247', '242-01248', '242-02729'],
  'Tile Concrete': ['242-01247', '242-01248', '242-02729'],
  Trapezoidal: ['242-10042-USA', '242-10044-USA', '242-01223', '242-01239'],
  'Wood/Shake Shingle': ['242-10016', '242-10017', '242-10018', '242-01223', '242-01239'],
}

// Roof type → default attachment SKU (from Slab "Reasonable default" column)
const ROOF_TYPE_DEFAULT_ATTACHMENT: Partial<Record<RoofTypeName, string>> = {
  'Composition / Asphalt Shingle': '242-10058', // UltraFoot™, Anchor, Black
  'Flat Concrete': '242-10018', // Ultra Rail Flush-Mount Kit, 8 1⁄2"
  'Flat Foam': '242-10018',
  'Membrane EPDM': '242-10018',
  'Membrane PVC': '242-10018',
  'Membrane TPO': '242-10018',
  'Metal Shingle': '242-10056', // UltraFoot™, Rafter, Black
  'Metal Standing Seam': '242-05159', // Ultra Rail Wide Base Seam Clamp
  'Metal Tin': '242-10039-USA', // All Purpose L Foot ft. S-5! Corrubracket (USA)
  'Rolled Asphalt': '242-10058', // UltraFoot, Anchor
  'Tar and Gravel / Bitumen': '242-10018',
  'Tile Clay': '242-02729', // Adjustable Tile Hook
  'Tile Concrete': '242-02729',
  Trapezoidal: '242-10042-USA', // S-5! ProteaBracket with Ultra Rail hardware
  'Wood/Shake Shingle': '242-10018',
}

export function getDefaultAttachmentCode(roofType: RoofTypeName | null): string | null {
  if (roofType == null) return null
  return ROOF_TYPE_DEFAULT_ATTACHMENT[roofType] ?? null
}

export const ULTRA_RAIL_COMPONENT_TITLES: Record<string, string> = {
  '242-02168': '5/16" Sealing Lag Screw',
  '242-02729': 'Adjustable Tile Hook',
  '242-05159': 'Ultra Rail Wide Base Seam Clamp',
  '242-10016': 'Ultra Rail Flush-Mount Kit, 5 1/2"',
  '242-10017': 'Ultra Rail Flush-Mount Kit, 7"',
  '242-10018': 'Ultra Rail Flush-Mount Kit, 8 1/2"',
  '242-10020': 'Ultra Rail Tilt-Mount Kit, 10"',
  '242-10021': 'Ultra Rail Tilt-Mount Kit, 14"',
  '242-10022': 'Ultra Rail Tilt-Mount Kit, 23"',
  '242-10035': 'DeckAnchor Screw',
  '242-10038': 'Ultra Rail AnchorFoot',
  '242-10039-USA': 'All Purpose L Foot ft. S-5! Corrubracket (USA)',
  '242-10041-USA': 'All Purpose L Foot ft. S-5! SolarFoot (USA)',
  '242-10042-USA': 'Ultra Rail Mounting Clamp feat. S-5! Protea Bracket (USA)',
  '242-10043-USA': 'All Purpose L Foot ft. S-5! S-5-N Mini (USA)',
  '242-10044-USA': 'All Purpose L Foot ft. S-5! VersaBracket-47 (USA)',
  '242-10056': 'UltraFoot, Rafter, Black',
  '242-10057': 'UltraFoot, Deck, Black',
  '242-10058': 'UltraFoot, Anchor, Black',
  '242-01220': 'Ultra Rail Umbrella L Foot, Black',
  '242-01223': 'Ultra Rail All Purpose L Foot (90°), Silver',
  '242-01239': 'Ultra Rail All Purpose L Foot (90°), Black',
  '242-01247': 'Ultra Rail Tile Roof Hook F',
  '242-01248': 'Ultra Rail Tile Roof Hook WS',
  '242-02158': 'Ultra Rail Metal Roof Base w/ L Foot',
  '242-02163': 'UR SpeedSeal Foot, Silver',
  '242-02167': 'UR SpeedSeal Foot, Black',
  '242-92266': 'Umbrella Lag Screw',
  '242-10066': '#14 Sealing Wood Screw',
  'Type 17-AB Milled Point': 'Type 17-AB Milled Point Screw',
  'Self Drilling Screw - Hex Washer Head': 'Self Drilling Screw - Hex Washer Head',
  'Hex head with EPDM rubber sealing washer': 'Hex head with EPDM rubber sealing washer',
  '232-10140': 'Ultra Rail Mid Clamp',
  '232-10141': 'Ultra Rail End Clamp',
}

const ROOF_TYPE_COMPATIBLE_STYLES: Partial<Record<RoofTypeName, UltraRailMountingType[]>> = {
  'Composition / Asphalt Shingle': ['Rafter', 'Decking'],
  'Flat Concrete': ['Rafter', 'Decking'],
  'Flat Foam': ['Rafter', 'Decking'],
  'Membrane EPDM': ['Rafter', 'Decking'],
  'Membrane PVC': ['Rafter', 'Decking'],
  'Membrane TPO': ['Rafter', 'Decking'],
  'Metal Shingle': ['Rafter', 'Decking', 'Metal'],
  'Metal Standing Seam': ['Rafter', 'Decking', 'Metal'],
  'Metal Tin': ['Rafter', 'Decking', 'Metal'],
  'Rolled Asphalt': ['Rafter', 'Decking'],
  'Tar and Gravel / Bitumen': ['Rafter', 'Decking'],
  'Tile Clay': ['Rafter', 'Decking'],
  'Tile Concrete': ['Rafter', 'Decking'],
  Trapezoidal: ['Rafter', 'Decking', 'Metal'],
  'Wood/Shake Shingle': ['Rafter', 'Decking'],
}

export function getCompatibleStylesForRoofType(roofType: RoofTypeName | null): UltraRailMountingType[] {
  if (!roofType) return ['Rafter', 'Decking', 'Metal']
  return ROOF_TYPE_COMPATIBLE_STYLES[roofType] ?? []
}

/**
 * Returns attachment options for roof type ∩ attachment style (Slab roof compatibility × fastener matrix).
 */
export function getAttachmentOptionsForRoofAndStyle(
  roofType: RoofTypeName,
  mountingType: UltraRailMountingType
): AttachmentOption[] {
  const compatibleStyles = ROOF_TYPE_COMPATIBLE_STYLES[roofType]
  if (!compatibleStyles?.includes(mountingType)) return []

  const roofAttachments = ROOF_TYPE_COMPATIBLE_ATTACHMENTS[roofType]
  if (!roofAttachments?.length) return []

  const styleAttachments = new Set(ATTACHMENTS_PER_STYLE[mountingType])
  return roofAttachments
    .filter((code) => styleAttachments.has(code))
    .map((code) => ({
      code,
      label: ULTRA_RAIL_COMPONENT_TITLES[code] || code,
    }))
}

type PZSEAttachmentByMounting = Partial<Record<UltraRailMountingType, string>>

/**
 * PZSE pv-racking Rail Mounts catalog names (SKU → API label).
 */
const PZSE_ROOF_ATTACHMENT_NAMES: Record<string, string | PZSEAttachmentByMounting> = {
  '242-10056': 'UltraFoot Rafter - 1 Lag',
  '242-10057': {
    Rafter: 'UltraFoot Deck - 2 wood screws-rafter',
    Decking: 'UltraFoot Deck - 4 wood screws',
    Metal: 'UltraFoot Deck - 2 wood screws-rafter',
  },
  '242-10058': {
    Rafter: 'UltraFoot Anchor - 1 Lag',
    Decking: 'UltraFoot Anchor - 2 SNR anchors-deck',
    Metal: 'UltraFoot Anchor - 1 Lag',
  },
  '242-01220': 'Umbrella L-Foot',
  '242-01223': 'All Purpose L-Foot (90 Deg)',
  '242-01239': 'All Purpose L-Foot (90 Deg)',
  '242-01247': 'Tile Roof Hook F',
  '242-01248': {
    Rafter: 'Tile Roof Hook WS',
    Decking: 'SpeedSeal DeckFoot - Deck Mount (ES)',
    Metal: 'Tile Roof Hook WS',
  },
  '242-02158': 'Metal Roof Base with L-Foot',
  '242-02163': 'SpeedSeal Foot',
  '242-02167': 'SpeedSeal Foot',
  '242-02729': 'Ultra Rail Adjustable Tile Hook',
  '242-10016': '4-Hole Base with 5-1/2 in Standoff',
  '242-10017': '1-Hole Base with 5-1/2 in, 7 in, or 8-1/2 in Standoff',
  '242-10018': {
    Rafter: 'OmniBase with 8-1/2 in Standoff - Rafter Mount',
    Decking: '6-OmniBase with 8-1/2 in Standoff - Deck Mount',
    Metal: 'OmniBase with 8-1/2 in Standoff - Rafter Mount',
  },
  '242-10020': 'OmniBase with 10 in Standoff - Rafter Mount',
  '242-10021': 'OmniBase with 14 in Standoff - Rafter Mount',
  '242-10022': 'OmniBase with 23 in Standoff - Rafter Mount',
  '242-10038': {
    Rafter: 'AnchorFoot w wood screws - Rafter Mount',
    Decking: 'AnchorFoot w wood screws - Deck Mount',
    Metal: 'AnchorFoot w wood screws - Rafter Mount',
  },
  '242-05159': 'Wide Base Seam Clamp with L-Foot',
  '242-10039-USA': 'CorruBracket 4 Wood Member, (4) Screws',
  '242-10041-USA': 'SolarFoot 4 Wood Member, (4) Screws',
  '242-10042-USA': 'Protea Bracket Vertical 4 24ga (.024") Steel',
  '242-10043-USA': 'N-mini Generic Generic 26ga (.018") Steel',
  '242-10044-USA': 'VersaBracket 47 3 Wood Member, (3) Screws',
}

function resolvePzseAttachmentName(
  mapping: string | PZSEAttachmentByMounting,
  mountingType: UltraRailMountingType
): string {
  if (typeof mapping === 'string') return mapping
  return mapping[mountingType] ?? mapping.Rafter ?? Object.values(mapping)[0] ?? ''
}

/**
 * PZSE pv-racking catalog name for SnapNRack roof attachments (SKU → API label).
 * Unmapped SKUs fall back to ULTRA_RAIL_COMPONENT_TITLES, then the raw SKU code.
 */
export function getPZSERoofAttachmentName(
  attachmentCode: string | undefined | null,
  mountingType: UltraRailMountingType = 'Rafter'
): string {
  if (!attachmentCode) return 'SpeedSeal Foot'
  const mapped = PZSE_ROOF_ATTACHMENT_NAMES[attachmentCode]
  if (mapped) return resolvePzseAttachmentName(mapped, mountingType)
  const title = ULTRA_RAIL_COMPONENT_TITLES[attachmentCode]
  if (title) return title
  return attachmentCode
}
